import { creating, exist, Id } from '@campaign-portal/namespace/common/id';
import {
	SenderSubscriptionClientStatus,
	SenderSubscriptionEnabledStatus,
	SenderSubscriptionEventStatus,
	TrafficType,
} from '@campaign-portal/namespace/common/enums';
import {
	SenderSubscription,
	SenderSubscriptionWhatsappSettings,
} from '@campaign-portal/namespace/entities/sender-id/specs';
import { FileInfo } from '@campaign-portal/namespace/common/fileInfo';

export interface SenderSubscriptionEntityConfig<ID = exist | creating> extends Partial<SenderSubscription<ID>> {
	id: ID;
}

export class SenderSubscriptionEntity<ID = exist | creating> implements SenderSubscription<ID> {
	[key: string]: unknown;

	id: ID;
	name: string;
	trafficType: TrafficType;
	subscribers: Id<exist>[];
	enabled: SenderSubscriptionEnabledStatus;
	activeFrom: string | null;
	activeTill: string | null;
	comment?: string | null;

	creationDate?: string | null;
	createdBy?: string | null;
	lastUpdated?: string | null;
	modifiedBy?: string | null;

	documents?: FileInfo[];
	partner?: Id;
	customFormIds?: Id<exist>[];
	lastEvent?: SenderSubscriptionEventStatus;
	status?: SenderSubscriptionClientStatus;
	whatsappSetting?: SenderSubscriptionWhatsappSettings;
	customFields?: { [fieldVariable: string]: unknown };

	constructor(config: SenderSubscriptionEntityConfig<ID>) {
		this.id = config.id;
		this.name = config.name ?? '';
		this.trafficType = config.trafficType ?? TrafficType.SMS;
		this.subscribers = config.subscribers ?? [];
		this.enabled = config.enabled ?? SenderSubscriptionEnabledStatus.ENABLED;
		this.activeFrom = config.activeFrom ?? null;
		this.activeTill = config.activeTill ?? null;
		this.comment = config.comment;
		this.creationDate = config.creationDate;
		this.createdBy = config.createdBy;
		this.lastUpdated = config.lastUpdated;
		this.modifiedBy = config.modifiedBy;
		this.documents = config.documents;
		this.partner = config.partner;
		this.customFormIds = config.customFormIds;
		this.lastEvent = config.lastEvent;
		this.status = config.status;
		this.whatsappSetting = config.whatsappSetting;
		this.customFields = config.customFields;
	}

	isEnabled(): boolean {
		return this.enabled === SenderSubscriptionEnabledStatus.ENABLED;
	}

	isDisabledByAdmin(): boolean {
		return this.enabled === SenderSubscriptionEnabledStatus.DISABLED_BY_ADMIN;
	}

	isDisabledByPartner(): boolean {
		return this.enabled === SenderSubscriptionEnabledStatus.DISABLED_BY_PARTNER;
	}

	isExpired(): boolean {
		if (!this.activeTill) {
			return false;
		}
		return new Date(this.activeTill) < new Date();
	}

	isNotExpired(): boolean {
		return !this.isExpired();
	}

	isInProgress(): boolean {
		return this.lastEvent === SenderSubscriptionEventStatus.IN_PROGRESS;
	}

	isDeclined(): boolean {
		return this.lastEvent === SenderSubscriptionEventStatus.DECLINED;
	}

	isActive(): boolean {
		if (!this.isEnabled()) {
			return false;
		}
		if (
			this.lastEvent &&
			![SenderSubscriptionEventStatus.CONFIRMED, SenderSubscriptionEventStatus.PERIOD_CHANGED].includes(this.lastEvent)
		) {
			return false;
		}
		return this.isNotExpired();
	}
}
